import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import './css/popup.css';
import { Translator, resources } from './Translator';

const translator = new Translator(resources);

const GridDivisionPopup = ({ onConfirm, onCancel, widthMeters, heightMeters,
    defaultMethod, defaultEqualDivisions, defaultDistanceInterval, defaultFontSize,
    defaultLabelMode
}) => {
    const t = (key) => translator.t(key);
    const [method, setMethod] = useState(defaultMethod || 'equal');
    const [equalDivisions, setEqualDivisions] = useState(defaultEqualDivisions || 4);
    const [distanceInterval, setDistanceInterval] = useState(defaultDistanceInterval || 100);
    const [error, setError] = useState('');

    const [fontSize, setFontSize] = useState(defaultFontSize || 28);
    const [labelMode, setLabelMode] = useState(defaultLabelMode || 'exterior_aux');

    const popupRef = useRef(null);

    useEffect(() => {
        if (popupRef.current) {
            const { width, height } = popupRef.current.getBoundingClientRect();
            popupRef.current.style.setProperty('--popup-width', `${width}px`);
            popupRef.current.style.setProperty('--popup-height', `${height}px`);
        }
    }, []);

    const calcEqualRows = (cols) => {
        if (!widthMeters || widthMeters < 1) return cols;
        return Math.max(1, Math.round(cols * heightMeters / widthMeters));
    };

    const calcDistanceDivisions = (interval) => {
        if (!interval || interval <= 0) return { cols: 0, rows: 0 };
        const cols = Math.min(702, Math.ceil(widthMeters / interval));
        const rows = Math.min(702, Math.ceil(heightMeters / interval));
        return { cols, rows };
    };

    const handleConfirm = () => {
        setError('');
        const style = {
            fontSize: parseInt(fontSize, 10) || 28,
            labelMode,
        };
        if (method === 'equal') {
            const cols = parseInt(equalDivisions, 10);
            if (isNaN(cols) || cols < 2 || cols > 702) {
                setError('2〜702の範囲で入力してください');
                return;
            }
            onConfirm(method, cols, style);
        } else {
            const interval = parseFloat(distanceInterval);
            if (isNaN(interval) || interval < 10 || interval > 10000) {
                setError('10〜10000mの範囲で入力してください');
                return;
            }
            if (interval > widthMeters || interval > heightMeters) {
                setError(t('Interval too large'));
                return;
            }
            onConfirm(method, interval, style);
        }
    };

    const rows = calcEqualRows(parseInt(equalDivisions, 10) || 4);
    const distDiv = calcDistanceDivisions(parseFloat(distanceInterval) || 100);

    const rowStyle = { display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' };
    const labelStyle = { flex: '0 0 90px', fontSize: '0.9em', textAlign: 'left' };
    const valStyle = { fontSize: '0.85em', color: '#cde', minWidth: '36px', textAlign: 'right' };

    const labelModeOptions = [
        { value: 'exterior', label: t('Exterior Only') },
        { value: 'exterior_aux', label: t('Exterior + Aux') },
        { value: 'interior', label: t('Interior All') },
    ];

    return (
        <Draggable handle=".popup-header">
        <div className="popup palette_popup" ref={popupRef}>
            <div className="popup-inner">
                <div className="popup-header">
                    <h2>{t('Grid Division')}</h2>
                    <span className="close_button" onClick={onCancel}>&times;</span>
                </div>

                {/* 分割方法 */}
                <div style={{ textAlign: 'left', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                        <input
                            type="radio" name="gridMethod" value="equal"
                            checked={method === 'equal'}
                            onChange={() => { setMethod('equal'); setError(''); }}
                            style={{ width: 'auto', margin: 0 }}
                        />
                        {t('Equal Division')}
                    </label>
                    {method === 'equal' && (
                        <div style={{ marginLeft: '24px', marginBottom: '8px' }}>
                            <label>{t('Horizontal Divisions')}</label>
                            <input
                                type="number" min={2} max={702} value={equalDivisions}
                                onChange={e => { setEqualDivisions(e.target.value); setError(''); }}
                                style={{ width: '80px' }}
                            />
                            <div style={{ fontSize: '0.85em', color: '#cde', marginTop: '4px' }}>
                                → {parseInt(equalDivisions, 10) || 4} × {rows}
                            </div>
                        </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                        <input
                            type="radio" name="gridMethod" value="distance"
                            checked={method === 'distance'}
                            onChange={() => { setMethod('distance'); setError(''); }}
                            style={{ width: 'auto', margin: 0 }}
                        />
                        {t('Distance Division')}
                    </label>
                    {method === 'distance' && (
                        <div style={{ marginLeft: '24px', marginBottom: '8px' }}>
                            <label>{t('Interval (m)')}</label>
                            <input
                                type="number" min={10} max={10000} value={distanceInterval}
                                onChange={e => { setDistanceInterval(e.target.value); setError(''); }}
                                style={{ width: '100px' }}
                            />
                            <div style={{ fontSize: '0.85em', color: '#cde', marginTop: '4px' }}>
                                → {distDiv.cols} × {distDiv.rows}
                            </div>
                        </div>
                    )}
                </div>

                {/* ラベルスタイル */}
                <div style={{ borderTop: '1px solid #7a9aaa', paddingTop: '10px', marginBottom: '12px' }}>
                    <div style={rowStyle}>
                        <span style={labelStyle}>{t('Label Font Size')}</span>
                        <input type="number" min={8} max={72} value={fontSize}
                            onChange={e => setFontSize(e.target.value)}
                            style={{ width: '60px' }} />
                    </div>
                    <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
                        <span style={labelStyle}>{t('Label Mode')}</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {labelModeOptions.map(opt => (
                                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}>
                                    <input type="radio" name="labelMode" value={opt.value}
                                        checked={labelMode === opt.value}
                                        onChange={() => setLabelMode(opt.value)}
                                        style={{ width: 'auto', margin: 0 }} />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {error && (
                    <div style={{ color: '#f88', marginBottom: '8px', fontSize: '0.9em' }}>{error}</div>
                )}

                <button className="button_b" onClick={onCancel}>{t('Cancel')}</button>
                <button className="button_y" onClick={handleConfirm}>{t('Confirm')}</button>
            </div>
        </div>
        </Draggable>
    );
};

export default GridDivisionPopup;
